import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
    Guid,
    IDeleteEntity,
    IGenericResponse,
    IStatusMessage,
    ResponseStatus
} from '../shared';
import { LoggingService } from '../shared/services/logging/logging.service';
import { IAuthorizedTask } from './IAuthorizedTask';
import { IDirectReportsWithTimeStatus } from './IDirectReportsWithTimeStatus';
import { IManagementAction } from './IManagementAction';
import { ITimeCard } from './itime-card';
import { ITimeCardSubmit } from './ITimeCardSubmit';
import { IWeeklySummary } from './IWeeklySummary';
import { TimeCardDocumentation } from './store/time-card-documentation.model';

@Injectable({
    providedIn: 'root'
})
export class TimeCardService {
    private uri = `${environment.apiLocation}/TimeCard`;
    private submitUri = `${environment.apiLocation}/TimeCardSubmit`;
    private taskUri = `${environment.apiLocation}/UserAuthorizedTask`;
    private tcwsUri = `${environment.apiLocation}/TimeCardWeeklySummary`;
    private drUri = `${environment.apiLocation}/DirectReportwithTimeStatus`;
    private maUri = `${environment.apiLocation}/TimeCardManagementAction`;
    private docUri = `${environment.apiLocation}/TimeCardDoc`;
    private policyUri = `${environment.apiLocation}/UserTimePolicyAccept`;
    constructor(private http: HttpClient, private log: LoggingService) {}

    public get(
        EmployeeID: string,
        TimeCardDate: string,
        MyDirection: string,
        Processid?: number,
        MinEntryDate?: Date,
        EndDate?: Date
    ): Observable<Array<ITimeCard>> {
        return this.http
            .post<Array<ITimeCard>>(this.uri, {
                EmployeeListID: EmployeeID,
                TimeCardDate,
                MyDirection,
                Processid,
                EndDate
            })
            .pipe(
                tap(data =>
                    this.log.verbose({ type: 'TimeCardService:get', data })
                ),
                map(data => data.map(item => this.mapTimecard(item))),
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:get:transformed',
                        data
                    })
                )
            );
    }

    public getTasks(
        EmployeeID: string,
        AuthType: string,
        AsOfDate: Date
    ): Observable<Array<IAuthorizedTask>> {
        return this.http
            .post<Array<IAuthorizedTask>>(this.taskUri, {
                EmployeeListID: EmployeeID,
                AuthType: AuthType,
                AsOfDate: AsOfDate
            })
            .pipe(
                tap(data =>
                    this.log.verbose({ type: 'TimeCardService:getTasks', data })
                ),
                map(data =>
                    data.map(x => ({ ...x, id: x.id ? x.id : Guid.newGuid() }))
                )
            );
    }

    public getWeeklySummary(
        EmployeeListID: string,
        DRListID?: string,
        EmpAuthID?: number,
        StartDate?: Date,
        EndDate?: Date
    ): Observable<Array<IWeeklySummary>> {
        return this.http
            .post<Array<IWeeklySummary>>(this.tcwsUri, {
                EmployeeListID: EmployeeListID,
                DRListID: DRListID,
                EmpAuthID: EmpAuthID,
                StartDate: StartDate,
                EndDate: EndDate
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:getWeeklySummary',
                        data
                    })
                ),
                map(data =>
                    data.map(x => ({ ...x, id: x.id ? x.id : Guid.newGuid() }))
                )
            );
    }

    public getDirectReportsWithTimeStatus(
        UserListID: string,
        UserRole: number,
        EmployeeListID?: string,
        StartDate?: string,
        EndDate?: string,
        MyDRsOnly?: boolean
    ): Observable<Array<IDirectReportsWithTimeStatus>> {
        return this.http
            .post<Array<IDirectReportsWithTimeStatus>>(this.drUri, {
                UserListID: UserListID,
                UserRole: UserRole,
                EmployeeListID: EmployeeListID,
                StartDate: StartDate,
                EndDate: EndDate,
                MyDRsOnly: MyDRsOnly
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:getDirectReportsWithTimeStatus',
                        data
                    })
                ),
                map(data =>
                    data.map(x => ({ ...x, id: x.id ? x.id : Guid.newGuid() }))
                )
            );
    }

    public putManagementAction(
        managementData: any
    ): Observable<IGenericResponse<IManagementAction>> {
        return this.http
            .put<IGenericResponse<IManagementAction>>(this.maUri, {
                TimeCardManagementActionList: [managementData]
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:putManagementAction',
                        data
                    })
                )
            );
    }

    public put(
        timeCardData: Array<ITimeCard>
    ): Observable<IGenericResponse<ITimeCard>> {
        this.log.verbose({ type: 'TimeCardService:put', timeCardData });
        return this.http
            .put<IGenericResponse<ITimeCard>>(this.uri, {
                Changes: { TimeCardList: timeCardData }
            })
            .pipe(
                tap(data =>
                    this.log.verbose({ type: 'TimeCardService:put:resp', data })
                ),
                map(resp => ({
                    ...resp,
                    Items: resp.Items.map(item => this.mapTimecard(item))
                })),
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:put:resp:mapped',
                        data
                    })
                )
            );
    }

    public deleteTimecardDocumentation(ids: Array<number | string>) {
        this.log.verbose({ type: 'deleteTimecardDocumentation', ids });

        return this.http
            .request<IGenericResponse<IDeleteEntity>>('DELETE', this.docUri, {
                body: { EntityIDs: ids }
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'deleteTimecardDocumentation:response',
                        data
                    })
                )
            );
    }

    public getUserTimePolicyAccept(): any {
        return this.http.get<any>(this.policyUri).pipe(res => {
            this.log.verbose({ type: 'getUserTimePolicyAccept:reponse', res });
            return res;
        });
    }

    public putTimeCardSubmit(
        timeCardSubmitData: Array<ITimeCardSubmit>
    ): Observable<IGenericResponse<ITimeCardSubmit>> {
        return this.http
            .put<IGenericResponse<ITimeCardSubmit>>(this.submitUri, {
                TimeCardSubmitList: timeCardSubmitData
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'TimeCardService:putTimeCardSubmit',
                        data
                    })
                )
            );
    }

    public getTimecardDocumentation(
        EmployeeListID: string,
        ProcessID: number
    ): Observable<Array<TimeCardDocumentation>> {
        return this.http
            .post<Array<TimeCardDocumentation>>(this.docUri, {
                EmployeeListID,
                ProcessID
            })
            .pipe(
                tap(data =>
                    this.log.verbose({ type: 'getTimecardDocumentation', data })
                ),
                map(a => a.map(x => this.mapTimecardDoc(x)))
            );
    }

    public putTimecardDocumentation(
        docs: Array<Partial<TimeCardDocumentation>>
    ): Observable<IGenericResponse<TimeCardDocumentation>> {
        this.log.verbose({ type: 'putTimecardDocumentation', docs });
        return this.http
            .put<IGenericResponse<TimeCardDocumentation>>(this.docUri, {
                TimeCardDocList: docs
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'putTimecardDocumentation:response',
                        data
                    })
                ),
                map(a => ({
                    ...a,
                    Items: a.Items.map(x => this.mapTimecardDoc(x)).map(x => ({
                        ...x,
                        Status: {
                            ...(x.Status as IStatusMessage),
                            StatusCode: ResponseStatus.Success
                        }
                    }))
                }))
            );
    }
    private mapTimecardDoc(item: TimeCardDocumentation): TimeCardDocumentation {
        return {
            ...item,
            id: `${item.EmpTimeID}-${item.WorkID}`,
            EmployeeWorkDate: moment(item.EmployeeWorkDate).toDate()
        };
    }

    private mapTimecard(item: ITimeCard): ITimeCard {
        return {
            ...item,
            id: item.EmpAuthID + item.WorkDateHdr
        };
    }
}
